import React from "react";
import { Users, Brain, Activity } from "lucide-react";
import { GAME_FORMULAS, GAME_CONSTANTS } from "../lib/gameLogic";
import "./GameDashboard.css";
import "./finance-metrics";
import StatBox from "./ui/statBox";
import FinanceMetrics from "./finance-metrics";
import { TooltipProvider } from "./ui/tooltip";
import Slider from "./ui/slider";
import GoalTracker from "./GoalTracker.tsx";

const GameDashboard = ({ gameState, gameActions }) => {
  const algorithmCost = GAME_FORMULAS.calculateAlgorithmCost(
    gameState.maxAlgorithmLevel
  );
  const overheadCost = GAME_FORMULAS.calculateOverheadCost(
    gameState.userCount,
    gameState.engagement,
    gameState.algorithmLevel,
    gameState.maxAlgorithmLevel
  );
  const userGrowth = GAME_FORMULAS.calculateUserGrowth(
    gameState.userCount,
    gameState.reputation
  );
  const revenue = GAME_FORMULAS.calculateRevenue(
    gameState.userCount,
    gameState.engagement,
    gameState.adFrequency,
    gameState.adPersonalization
  );

  const handleAdFrequencyChange = (newValue) => {
    const delta = newValue - gameState.adFrequency;
    gameActions.changeAdFrequency(delta);
  };

  const handleAdPersonalizationChange = (newValue) => {
    const delta = newValue - gameState.adPersonalization;
    gameActions.changeAdPersonalization(delta);
  };

  const handleAlgorithmLevelChange = (newValue) => {
    const delta = newValue - gameState.algorithmLevel;
    gameActions.changeAlgorithmLevel(delta);
  };

  return (
    <div className="dashboard">
      {gameState.showGoals && <GoalTracker gameState={gameState} />}

      <div>
        {/* <h2>Metrics</h2> */}
        <div className="stats-grid">
          <FinanceMetrics
            balance={Math.floor(gameState.money)}
            revenue={Math.floor(revenue)}
            overhead={Math.floor(overheadCost)}
            info="The amount of money you have. Revenue depends on how much your app is being used and your ad settings. Overhead depends on how much your app is being used and how sophisticated the algorithm is."
          />
          <div className="secondary-metrics">
            <StatBox
              icon={Users}
              label="Users"
              value={Math.floor(gameState.userCount).toLocaleString()}
              subValues={[
                {
                  label: "Growth",
                  value: Math.floor(userGrowth),
                  positive: userGrowth >= 0,
                },
              ]}
              info="The number of users on your platform. A better experience and lively user base brings even more users."
            />
            <StatBox
              icon={Activity}
              label="Engagement"
              value={`${gameState.engagement.toFixed(2)}x`}
              info="The average time users spend on your platform. Higher engagement means more ad revenue."
            />
          </div>
        </div>
      </div>

      {gameState.showControls && (
        <>
          {/* <h2>Controls</h2> */}
          <div className="controls-section">
            <TooltipProvider>
              <Slider
                label="Ad Frequency"
                value={gameState.adFrequency}
                onChange={handleAdFrequencyChange}
                info="How often users see ads while using your platform. Higher frequency brings in more revenue but decreases engagement and reputation."
              />

              {gameState.showAdPersonalization && (
                <Slider
                  label="Ad Personalization"
                  value={gameState.adPersonalization}
                  onChange={handleAdPersonalizationChange}
                  levelCap={
                    gameState.algorithmLevel *
                    GAME_CONSTANTS.AD_PERSONALIZATION_MULTIPLIER
                  }
                  disabled={
                    gameState.adPersonalization >=
                    gameState.algorithmLevel *
                      GAME_CONSTANTS.AD_PERSONALIZATION_MULTIPLIER
                  }
                  levelCapIndicator={
                    gameState.adPersonalization >=
                    gameState.algorithmLevel *
                      GAME_CONSTANTS.AD_PERSONALIZATION_MULTIPLIER
                      ? ` • Max for Algorithm Level ${gameState.algorithmLevel}`
                      : null
                  }
                  info="How targeted ads are to individual users. More personalized ads increase revenue but may scare off users if they're too specific. Requires algorithm upgrades to increase the maximum level."
                />
              )}

              {gameState.maxAlgorithmLevel > 1 && (
                <Slider
                  label={`Algorithm Level (${gameState.algorithmLevel}/${gameState.maxAlgorithmLevel})`}
                  value={gameState.algorithmLevel}
                  hideValue={true}
                  onChange={handleAlgorithmLevelChange}
                  min={1}
                  max={gameState.maxAlgorithmLevel}
                  step={1}
                  info="Set the algorithm's current level. Higher levels increase engagement but cost more to operate. Unlock new levels to enable more ad personalization."
                />
              )}
            </TooltipProvider>

            {gameState.showAlgorithmUpgrade && (
              <button
                onClick={() => gameActions.upgradeAlgorithm()}
                disabled={gameState.money < algorithmCost}
                className={`upgrade-button ${
                  gameState.money < algorithmCost ? "disabled" : ""
                }`}
              >
                <Brain />
                <span>
                  Advance Algorithm Research (${algorithmCost.toLocaleString()})
                </span>
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GameDashboard;
